import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import API_BASE_URL from '../../apis/api';
import {API_INTG_URL} from "../../apis/api"; 

const api = axios.create({
  baseURL: API_INTG_URL,
  headers: {
    "Content-Type": "application/json",
  },
});
export const STATUSES = {
  IDLE: "idle",
  LOADING: "loading",
  ERROR: "error",
};

// ✅ Axios interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      console.warn("⚠️ Access token expired. Attempting to refresh...");
      try {
        const refreshToken = localStorage.getItem("refresh_token");
        if (!refreshToken) throw new Error("No refresh token available");
        const response = await api.post("/api/auth/token/refresh/", {
          refresh: refreshToken,
        });
        const { access } = response.data;
        localStorage.setItem("access_token", access);
        setAuthToken(access);
        // Retry the failed request with the new token
        error.config.headers["Authorization"] = `Bearer ${access}`;
        return api.request(error.config);
      } catch (refreshError) {
        console.error("❌ Token refresh failed. Logging out...");
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        window.location.href = "/"; // Redirect to login
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// ✅ Function to set the Authorization token
export const setAuthToken = (token) => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  };




// ✅ Thunk to fetch documents for a specific caseNumber
export const fetchDocumentsByCase = createAsyncThunk(
  "documents/fetchByCase",
  async (caseNumber, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("No authentication token found. Please log in.");

      setAuthToken(token); // Set Authorization token

      const response = await api.get(`/api/supplychain/cases/${caseNumber}/documents/`);

      console.log("✅ Documents API Response:", response.data);

      // Ensure the response contains a valid documents array
      if (response.data && Array.isArray(response.data.documents)) {
        // Add token to the PDF link
        const documentsWithToken = response.data.documents.map((doc) => {
          const originalLink = doc.link.split("&")[0]; // Remove extra query params
          return {
            ...doc,
            link: `${originalLink}&token=${encodeURIComponent(token)}`, // Append token to link
          };
        });

        return { caseNumber, documents: documentsWithToken };
      } else {
        console.error("❌ Invalid API response structure:", response.data);
        return rejectWithValue("Invalid API response structure.");
      }
    } catch (error) {
      console.error("❌ Error fetching documents:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to fetch documents for a specific threadId
export const fetchDocumentsByThreadId = createAsyncThunk(
  "documents/fetchByThreadId",
  async (threadId) => {
    const response = await axios.get(`${API_BASE_URL}/caseLogs/${threadId}`);
    const documents = response.data.documents || [];
    return { threadId, documents };
  }
);






// ✅ Thunk to upload a new document for a specific caseNumber
export const uploadNewDocumentThunk = createAsyncThunk(
  "documents/uploadNewDocument",
  async ({ caseNumber, file, documentType, sentBy }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("No authentication token found. Please log in.");

      setAuthToken(token); // Set Authorization token

      // ✅ Prepare FormData for file upload
      const formData = new FormData();
      formData.append("file", file); // File upload
      formData.append("document_type", documentType); // e.g., "custom_clearance"
      formData.append("sent_by", sentBy); // e.g., "freight_forwarder"

      // ✅ API Call: Upload document
      const response = await api.post(`/api/supplychain/cases/${caseNumber}/documents/upload/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data", // Required for file uploads
        },
      },);

      console.log("✅ Document uploaded successfully:", response.data);

      return { caseNumber, document: response.data };
    } catch (error) {
      console.error("❌ Failed to upload document:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);





// ✅ Thunk to persist updates to a filled data template (ExtractedInfo)
export const updateFilledDataTemplateThunk = createAsyncThunk(
  "documents/updateFilledDataTemplate",
  async ({ caseNumber, documentId, extractedInfo }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("No authentication token found. Please log in.");
      setAuthToken(token); // Set Authorization token

      // ✅ Prepare the request payload
      const payload = JSON.stringify({ ExtractedInfo: extractedInfo });

      // ✅ Send PATCH request with updated ExtractedInfo
      const response = await api.patch(
        `/api/supplychain/cases/${caseNumber}/documents/${documentId}/update_extracted_info/`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log(`✅ ExtractedInfo updated for document ${documentId}:`, response.data);

      return { caseNumber, documentId, extractedInfo }; // ✅ Return updated data
    } catch (error) {
      console.error(`❌ Failed to update ExtractedInfo for document ${documentId}:`, error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


// ✅ Thunk to persist updates to a filled data template (ExtractedInfo)
export const updateMandatoryDataThunk = createAsyncThunk(
  "documents/updateMandatoryData",
  async ({ caseNumber, documentId, extractedInfo }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("No authentication token found. Please log in.");
      setAuthToken(token); // Set Authorization token

      // ✅ Prepare the request payload
      const payload = JSON.stringify({ MandatoryInfo: extractedInfo });

      // ✅ Send PATCH request with updated ExtractedInfo
      const response = await api.patch(
        `/api/supplychain/cases/${caseNumber}/documents/${documentId}/update_mandatory_info/`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log(`✅ ExtractedInfo updated for document ${documentId}:`, response.data);

      return { caseNumber, documentId, extractedInfo }; // ✅ Return updated data
    } catch (error) {
      console.error(`❌ Failed to update ExtractedInfo for document ${documentId}:`, error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);



const documentSlice = createSlice({
  name: "documents",
  initialState: {
    documents: {}, // { threadId: [{ documentId, document, filledDataTemplate }] }
    status: "idle",
    error: null,
  },
  reducers: {
    // Local update for a filled data template
    updateFilledDataTemplate: (state, action) => {
      const { threadId, documentId, templateData } = action.payload;
      const threadDocuments = state.documents[threadId];
      if (threadDocuments) {
        const document = threadDocuments.find((doc) => doc.documentId === documentId);
        if (document) {
          document.filledDataTemplate = templateData;
        }
      }
    },
    removeDocument: (state, action) => {
      const { threadId, documentId } = action.payload;
      if (state.documents[threadId]) {
        state.documents[threadId] = state.documents[threadId].filter(
          (doc) => doc.documentId !== documentId
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocumentsByCase.pending, (state) => {
        state.status = STATUSES.LOADING;
        state.error = null;
      })
      .addCase(fetchDocumentsByCase.fulfilled, (state, action) => {
        const { caseNumber, documents } = action.payload;
        state.documents[caseNumber] = documents; // Store documents under caseNumber key
        state.status = STATUSES.IDLE;
      })
      .addCase(fetchDocumentsByCase.rejected, (state, action) => {
        state.status = STATUSES.ERROR;
        state.error = action.payload || "Failed to fetch documents.";
      })
      .addCase(fetchDocumentsByThreadId.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchDocumentsByThreadId.fulfilled, (state, action) => {
        const { threadId, documents } = action.payload;
        state.documents[threadId] = documents;
        state.status = "succeeded";
      })
      .addCase(fetchDocumentsByThreadId.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(updateFilledDataTemplateThunk.fulfilled, (state, action) => {
        const { threadId, documentId, templateData } = action.payload;
        const threadDocuments = state.documents[threadId];
        if (threadDocuments) {
          const document = threadDocuments.find((doc) => doc.documentId === documentId);
          if (document) {
            document.filledDataTemplate = templateData;
          }
        }
      })
      .addCase(uploadNewDocumentThunk.pending, (state) => {
        state.status = "uploading";
      })
      .addCase(uploadNewDocumentThunk.fulfilled, (state, action) => {
        // if (!state.documents[caseNumber]) {
        //   state.documents[caseNumber] = [];
        // }
        // state.documents[caseNumber].push(document); // Append new document
        state.status = STATUSES.IDLE;
      })
      .addCase(uploadNewDocumentThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
  },
});

export const { updateFilledDataTemplate, removeDocument } = documentSlice.actions;

export default documentSlice.reducer;
